import { useMemo } from 'react'

import {
  F0Box,
  F0Button,
  F0Icon,
  F0TagStatus,
  F0Text,
  ProgressBarChart,
} from '@factorialco/f0-react'
import {
  ChartPie,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Clock,
  DottedCircle,
} from '@factorialco/f0-react/icons/app'
import { GoalsTabMetricFragment, GoalsTabMetricGroupFragment } from 'generated/resources/graphql'

import { parseBackendDate } from 'modules/core/components/DateNavigator/dateFormatUtils'
import i18n from 'modules/core/lib/i18n'

import { getStatusConfig } from 'modules/goals/lib/goalStatus'

import { formatDistanceToNowStrict } from 'date-fns'

const getDeadlineLabel = (deadline: Date | undefined): string | null => {
  return deadline ? formatDistanceToNowStrict(deadline, { addSuffix: true }) : null
}

const MetricRow = ({ metric }: { metric: GoalsTabMetricFragment }) => {
  const status = useMemo(
    () => getStatusConfig(metric.progressAssessmentStatus),
    [metric.progressAssessmentStatus]
  )

  const deadlineLabel = getDeadlineLabel(parseBackendDate(metric.endOn))

  return (
    <F0Box display="flex" flexDirection="row" gap="md" alignItems="start">
      <F0Box display="flex" flexDirection="column" alignItems="center" gap="xs">
        {metric.progressPercentageValue >= 100 ? (
          <F0Icon icon={CheckCircle} color="positive" />
        ) : (
          <F0Icon icon={DottedCircle} color="default" />
        )}
      </F0Box>

      <F0Box display="flex" flexDirection="column" gap="md" borderLeft="default" paddingLeft="md">
        <F0Text variant="body" content={metric.title} />

        <F0Box display="flex" flexDirection="row" alignItems="center" gap="sm">
          {status && <F0TagStatus text={status.label} variant={status.variant} />}

          <F0Box display="flex" flexDirection="row" gap="sm" alignItems="center">
            <F0Icon icon={Clock} color="secondary" />
            {deadlineLabel && <F0Text variant="description" content={deadlineLabel} />}
          </F0Box>
        </F0Box>
      </F0Box>
    </F0Box>
  )
}

const getLatestDeadlineFromSortedMetrics = (
  metrics: GoalsTabMetricFragment[]
): Date | undefined => {
  const last = metrics.at(-1)
  return last ? parseBackendDate(last.endOn) : undefined
}

export const GoalGroupCard = ({
  group,
  isExpanded,
  onToggle,
}: {
  group: GoalsTabMetricGroupFragment
  isExpanded: boolean
  onToggle: () => void
}) => {
  const metrics = group.metricsConnection?.nodes ?? []
  const averageProgress = group.summary?.averageProgress ?? 0
  const latestDeadline = getLatestDeadlineFromSortedMetrics(metrics)
  const goalStatus = group.summary?.status ? getStatusConfig(group.summary.status) : null
  const deadlineLabel = getDeadlineLabel(latestDeadline)
  const isExpandable = metrics.length > 1

  return (
    <F0Box
      display="flex"
      flexDirection="column"
      gap="lg"
      border="default"
      borderRadius="lg"
      padding="md"
    >
      <F0Box display="flex" flexDirection="row" justifyContent="between" alignItems="center">
        <F0Text variant="body" content={group.title} />

        {isExpandable && (
          <F0Button
            label={isExpanded ? i18n.t('common.collapse') : i18n.t('common.expand')}
            variant="ghost"
            icon={isExpanded ? ChevronUp : ChevronDown}
            hideLabel
            onClick={onToggle}
            size="sm"
          />
        )}
      </F0Box>

      <F0Box display="flex" flexDirection="row" alignItems="center" gap="xs">
        <F0Box display="flex" flexDirection="row" alignItems="center" gap="xl">
          {goalStatus && <F0TagStatus text={goalStatus.label} variant={goalStatus.variant} />}

          <F0Icon icon={ChartPie} color="secondary" />
        </F0Box>

        <F0Box width="full">
          <ProgressBarChart
            value={averageProgress}
            max={100}
            label={`${i18n.formatNumber(averageProgress)}%`}
            color="feedback-positive"
          />
        </F0Box>
      </F0Box>

      <F0Box display="flex" flexDirection="row" gap="sm" alignItems="center">
        <F0Icon icon={Clock} color="secondary" />
        {deadlineLabel && (
          <F0Text
            variant="description"
            content={`${i18n.t('performance_process.reviews.common.due_date')} ${deadlineLabel}`}
          />
        )}
      </F0Box>

      {isExpandable && isExpanded && (
        <F0Box display="flex" flexDirection="column" gap="lg">
          {metrics.map((metric) => (
            <MetricRow key={metric.id} metric={metric} />
          ))}
        </F0Box>
      )}
    </F0Box>
  )
}
